<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "../../Store/auth";
import { RouterLink, useRouter } from "vue-router";
import {
    ROLE_ADMINISTRATOR,
    ROLE_PUBLIC,
    ROLE_SUPERADMIN,
} from "../../Config/roleStructure";
import { AuthService } from "../../Services/Auth/auth.service";
import { API_URL, IS_PRODUCTION } from "../../Config/global";
import { Notification } from "../../Services/Administrator/notification.service";

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const router = useRouter();
const service = new Notification();

const canSeeNotifications = computed(() => {
    if (!user.value?.role) {
        return false;
    }
    return [ROLE_ADMINISTRATOR, ROLE_SUPERADMIN].includes(user.value.role.id);
});

const notifications = ref([]);
const unreadCount = ref(0);
const showNotifications = ref(false);
const loading = ref(false);
const initialLoad = ref(true);
const lastNotificationUpdate = ref(null);
const lastUnreadCount = ref(null);

const fetchNotifications = async (silent = false) => {
    try {
        if (!silent) {
            loading.value = true;
        }
        const [response, err] = await service.list();
        if (err) throw err;

        const newNotifications = response.data.notifications || [];
        const newUnreadCount = response.data.unread_count ?? 0;
        const notificationsChanged =
            JSON.stringify(notifications.value) !==
            JSON.stringify(newNotifications);
        const unreadCountChanged = unreadCount.value !== newUnreadCount;
        if (notificationsChanged) {
            notifications.value = newNotifications;
            lastNotificationUpdate.value = Date.now();
        }
        if (unreadCountChanged) {
            unreadCount.value = newUnreadCount;
            lastUnreadCount.value = newUnreadCount;
        }
        initialLoad.value = false;
    } catch (error) {
        console.error("Error fetching notifications:", error);
        initialLoad.value = false;
    } finally {
        if (!silent) {
            loading.value = false;
        }
    }
};

const fetchUnreadCount = async () => {
    try {
        const [response, err] = await service.unreadCount();
        if (err) throw err;
        const newUnreadCount = response.data.unread_count ?? 0;
        if (unreadCount.value !== newUnreadCount) {
            unreadCount.value = newUnreadCount;
            lastUnreadCount.value = newUnreadCount;
        }
    } catch (error) {
        console.error("Error fetching unread count:", error);
    }
};

const handleNotificationClick = async (notification) => {
    try {
        const [response, err] = await service.markAsRead(notification.id);
        if (err) throw err;

        if (response.data.success) {
            if (!notification.read_at) {
                unreadCount.value = Math.max(0, unreadCount.value - 1);
            }

            const notificationIndex = notifications.value.findIndex(
                (n) => n.id === notification.id
            );
            if (notificationIndex !== -1) {
                notifications.value[notificationIndex].read_at =
                    new Date().toISOString();
            }

            router.push(response.data.redirect_url);
            showNotifications.value = false;
        }
    } catch (error) {
        console.error("Error marking notification as read:", error);
    }
};

const markAllAsRead = async () => {
    try {
        const [response, err] = await service.markAllAsRead();
        if (err) throw err;
        if (response.data.success) {
            unreadCount.value = 0;
            await fetchNotifications();
        }
    } catch (error) {
        console.error("Error marking all as read:", error);
    }
};

const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
};

const handleClickOutside = (event) => {
    const notificationDropdown = document.getElementById(
        "notification-dropdown"
    );
    const notificationButton = document.getElementById("notification-button");

    if (
        notificationDropdown &&
        !notificationDropdown.contains(event.target) &&
        notificationButton &&
        !notificationButton.contains(event.target)
    ) {
        showNotifications.value = false;
    }
};

const toggleNotifications = async () => {
    showNotifications.value = !showNotifications.value;

    if (showNotifications.value && !loading.value) {
        await fetchNotifications(true);
    }
};

let refreshInterval, listInterval;

onMounted(() => {
    if (canSeeNotifications.value) {
        fetchNotifications();

        refreshInterval = setInterval(() => {
            if (!showNotifications.value) {
                fetchUnreadCount();
            } else {
                fetchNotifications(true);
            }
        }, 30000);
        listInterval = setInterval(() => {
            if (showNotifications.value && !loading.value) {
                fetchNotifications(true); // silent refresh
            }
        }, 30000);
    }

    document.addEventListener("click", handleClickOutside);

    // Toggle the side navigation
    const sidebarToggle = document.body.querySelector("#sidebarToggle");
    if (sidebarToggle) {
        // Uncomment Below to persist sidebar toggle between refreshes
        if (localStorage.getItem("sb|sidebar-toggle") === "true") {
            document.body.classList.toggle("sb-sidenav-toggled");
        }
        sidebarToggle.addEventListener("click", (event) => {
            event.preventDefault();
            document.body.classList.toggle("sb-sidenav-toggled");
            localStorage.setItem(
                "sb|sidebar-toggle",
                document.body.classList.contains("sb-sidenav-toggled")
            );
        });
    }
});

onUnmounted(() => {
    clearInterval(refreshInterval);
    clearInterval(listInterval);
    document.removeEventListener("click", handleClickOutside);
});

const logout = () => {
    const user = authStore.user;

    if (parseInt(user.role.is_configable) != 1 && IS_PRODUCTION) {
        const token = authStore.token;
        window.location = API_URL + "/auth/bruneiid/logout?token=" + token;
    } else {
        new AuthService().logout();
        authStore.logout();
        router.push({ name: "choose-role" });
    }
};
</script>

<template>
    <nav class="sb-topnav navbar navbar-expand bg-white shadow-sm">
        <!-- Navbar Brand-->
        <a class="navbar-brand ps-3 d-flex align-items-center" href="/">
            <img src="/assets/images/tds-logo.png" class="brand-image" />
            <img src="/assets/images/tims-logo.png" class="brand-image" />
        </a>
        <!-- Sidebar Toggle-->
        <button
            class="btn btn-link btn-sm order-1 order-lg-0 me-4 me-lg-0"
            id="sidebarToggle"
            href="#!"
        >
            <i class="fas fa-bars"></i>
        </button>
        <!-- Navbar Search-->
        <form
            class="d-none d-md-inline-block form-inline ms-auto me-0 me-md-3 my-2 my-md-0"
        ></form>
        <!-- Navbar-->
        <ul class="navbar-nav ms-auto ms-md-0 me-lg-4 align-items-center">
            <li
                v-if="canSeeNotifications"
                class="nav-item dropdown me-3 position-relative"
            >
                <button
                    id="notification-button"
                    class="btn btn-link nav-link p-2 position-relative"
                    @click="toggleNotifications"
                    type="button"
                >
                    <i class="fas fa-bell fa-fw"></i>
                    <span
                        v-if="unreadCount > 0"
                        class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                        style="font-size: 0.7rem"
                    >
                        {{ unreadCount > 99 ? "99+" : unreadCount }}
                        <span class="visually-hidden"
                            >unread notifications</span
                        >
                    </span>
                </button>

                <div
                    id="notification-dropdown"
                    v-show="showNotifications"
                    class="dropdown-menu dropdown-menu-end notification-dropdown show"
                    style="width: 350px; max-height: 400px; overflow-y: auto"
                >
                    <div
                        class="dropdown-header d-flex justify-content-between align-items-center"
                    >
                        <h6 class="mb-0">Notifications</h6>
                        <button
                            v-if="unreadCount > 0"
                            @click="markAllAsRead"
                            class="btn btn-sm btn-link p-0 text-primary"
                            style="font-size: 0.8rem"
                        >
                            Mark all read
                        </button>
                    </div>

                    <div v-if="loading" class="text-center p-3">
                        <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                        >
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>

                    <div
                        v-else-if="notifications?.length === 0"
                        class="text-center p-3 text-muted"
                    >
                        No notifications
                    </div>

                    <template v-else>
                        <div
                            v-for="notification in notifications"
                            :key="notification.id"
                            @click="handleNotificationClick(notification)"
                            class="dropdown-item notification-item"
                            :class="{ unread: !notification.read_at }"
                            style="
                                cursor: pointer;
                                white-space: normal;
                                padding: 12px 16px;
                            "
                        >
                            <div class="d-flex">
                                <div class="flex-grow-1">
                                    <div
                                        class="notification-title fw-semibold text-dark"
                                    >
                                        {{ notification.data.title }}
                                    </div>
                                    <div
                                        class="notification-message text-muted small"
                                    >
                                        {{ notification.data.message }}
                                    </div>
                                    <div
                                        class="notification-user small text-primary"
                                    >
                                        Role: {{ notification.data.user_role }}
                                    </div>
                                    <div
                                        class="notification-time small text-muted"
                                    >
                                        {{
                                            formatTime(notification.created_at)
                                        }}
                                    </div>
                                </div>
                                <div
                                    v-if="!notification.read_at"
                                    class="notification-dot"
                                >
                                    <span
                                        class="badge bg-primary rounded-pill"
                                        style="width: 8px; height: 8px"
                                    ></span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </li>

            <li class="nav-item dropdown">
                <a
                    class="nav-link d-flex align-items-center justify-content-center"
                    id="navbarDropdown"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                >
                    <span v-if="user.picture" class="profile-picture">
                        <img :src="user.picture" class="preview-picture" />
                    </span>
                    <i v-else class="fas fa-user fa-fw"></i>
                    <span class="ms-2 d-none d-lg-block">{{ user.name }}</span>
                </a>
                <ul
                    class="dropdown-menu dropdown-menu-end"
                    aria-labelledby="navbarDropdown"
                >
                    <li v-if="user.role.id != ROLE_PUBLIC">
                        <RouterLink
                            class="dropdown-item"
                            :to="{ name: 'profile' }"
                        >
                            Profile
                        </RouterLink>
                    </li>
                    <li v-if="user.role.id != ROLE_PUBLIC">
                        <hr class="dropdown-divider" />
                    </li>
                    <li>
                        <a class="dropdown-item" href="#" @click="logout">
                            {{
                                user.role.id != ROLE_PUBLIC ? "Logout" : "Login"
                            }}
                        </a>
                    </li>
                </ul>
            </li>
        </ul>
    </nav>
</template>

<style scoped>
.has-line {
    position: relative;
}

.has-line::after {
    content: "";
    position: absolute;
    border-right: 1px solid #000;
    top: 0px;
    bottom: 0px;
}

.notification-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    width: 350px;
    max-width: 90vw;
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    background-color: white;
    border-radius: 0.375rem;
    z-index: 1050;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: all 0.2s ease;
}

.dropdown-fade-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

@media (max-width: 576px) {
    .notification-dropdown {
        width: 300px;
        right: -50px;
    }
}

@media (max-width: 480px) {
    .notification-dropdown {
        width: 280px;
        right: -80px;
    }
}

.notification-item {
    border-bottom: 1px solid #f8f9fa;
    transition: background-color 0.2s;
    cursor: pointer;
    white-space: normal;
    padding: 12px 16px;
}

.notification-item:hover {
    background-color: #f8f9fa;
}

.notification-item.unread {
    background-color: #f0f8ff;
}

.notification-item.unread:hover {
    background-color: #e6f3ff;
}

.notification-dot {
    align-self: flex-start;
    margin-top: 4px;
    margin-left: 8px;
}

.notification-title {
    font-size: 0.9rem;
    line-height: 1.3;
}

.notification-message {
    font-size: 0.8rem;
    line-height: 1.2;
    margin-bottom: 2px;
}

.notification-user {
    font-size: 0.75rem;
    line-height: 1.2;
    margin-bottom: 2px;
}

.notification-time {
    font-size: 0.7rem;
    line-height: 1.2;
}

.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fa-bell {
    font-size: 1.3rem;
}
</style>
